import { FilterOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  InvoiceStatusEnum,
  PaymentFrequencyEnum,
} from '@nxsys/constants/AppEnums';
import { useSaveRunPayroll } from '@nxsys/hooks/api/timesheets';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { AppState } from '@nxsys/store';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { getSelectPayrollColumns } from '../listing/column';
import SelectPayrollFilterModal, {
  SelectPayrollFilterValues,
} from './SelectPayrollFilterModal';

type SelectPayrollModalProps = {
  isOpen: boolean;
  toggle: () => void;
  payFrequency: string | null;
  taxPeriod: number | null;
  onSuccess?: () => void;
};

type LocalPagination = {
  current: number;
  pageSize: number;
  totalDocs: number;
};

const SelectPayrollModal: React.FC<SelectPayrollModalProps> = ({
  isOpen,
  toggle,
  payFrequency,
  taxPeriod,
  onSuccess,
}) => {
  const { messages } = useTypedIntl();
  const theme = useTheme();
  const { payrollCountry, isAdmin, isBureauEmployer, user, isBureau } =
    useSelector((state: AppState) => state.appStore);

  const { run: saveRunPayroll, loading: isSaveRunPayrollPending } =
    useSaveRunPayroll();

  const [data, setData] = useState<TimeSheetDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<LocalPagination>({
    current: 0,
    pageSize: 20,
    totalDocs: 0,
  });
  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const searchRef = useRef('');
  const companyRefsRef = useRef<string[]>([]);
  const filterRef = useRef<SelectPayrollFilterValues>({});

  const fetchBureauTimesheets = useCallback(
    async (params: {
      page: number;
      size: number;
      payFrequency: string;
      taxPeriod: number;
      search?: string;
      companyRefs?: string[];
      filter?: SelectPayrollFilterValues;
    }) => {
      setLoading(true);
      try {
        const effectiveCompanyRefs =
          isBureauEmployer && user?.companyUserIds?.length
            ? user.companyUserIds
            : params.companyRefs;

        const finalParams = {
          ...params,
          invoiceStatus: InvoiceStatusEnum.AWAITING_DRAFT,
          onlySavedRunPayroll: false,
          paymentModel: 'PAYEE',
          ...(params?.payFrequency === PaymentFrequencyEnum.MONTHLY
            ? { monthConfig: params?.taxPeriod }
            : {}),
          ...(params?.search ? { search: params.search } : {}),
          ...(effectiveCompanyRefs?.length
            ? { companyRefs: effectiveCompanyRefs }
            : {}),
          ...(isBureauEmployer ? { noFilterByApprover: true } : {}),
          ...(params.filter ?? {}),
        };
        delete (finalParams as any).filter;
        const response = await jwtAxios.get(
          endPoints.timesheet.bureauTimesheet,
          {
            params: finalParams,
          },
        );
        const resData = response.data;
        const timesheetList = (resData.data ?? []).map(
          (item: TimeSheetDataItem) => ({
            ...item,
            isSalary: false,
          }),
        );
        const salariedList = (resData.salariedEmployees ?? []).map(
          (item: TimeSheetDataItem) => ({
            ...item,
            isSalary: true,
          }),
        );
        setData([...salariedList, ...timesheetList]);
        setPagination({
          current: params.page,
          pageSize: params.size,
          totalDocs: (salariedList.length || 0) + (resData.totalDocs || 0),
        });
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isOpen && payFrequency && taxPeriod) {
      searchRef.current = '';
      companyRefsRef.current = [];
      filterRef.current = {};
      setSearchText('');
      fetchBureauTimesheets({
        page: 0,
        size: 20,
        payFrequency,
        taxPeriod,
      });
      setSelectedRows([]);
    }
  }, [isOpen, payFrequency, taxPeriod]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (!payFrequency || !taxPeriod) return;
      searchRef.current = value;
      fetchBureauTimesheets({
        page: 0,
        size: pagination.pageSize,
        payFrequency,
        taxPeriod,
        search: value,
        companyRefs: companyRefsRef.current,
        filter: filterRef.current,
      });
    }, 300),
    [payFrequency, taxPeriod, pagination.pageSize],
  );

  const handleCompanyChange = (value: any) => {
    const refs = value?.map((item: any) => item.value) ?? [];
    companyRefsRef.current = refs;
    if (!payFrequency || !taxPeriod) return;
    fetchBureauTimesheets({
      page: 0,
      size: pagination.pageSize,
      payFrequency,
      taxPeriod,
      search: searchRef.current,
      companyRefs: refs,
      filter: filterRef.current,
    });
  };

  const handleFilter = (values: SelectPayrollFilterValues) => {
    filterRef.current = values;
    if (!payFrequency || !taxPeriod) return;
    fetchBureauTimesheets({
      page: 0,
      size: pagination.pageSize,
      payFrequency,
      taxPeriod,
      search: searchRef.current,
      companyRefs: companyRefsRef.current,
      filter: values,
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedSearch(e.target.value);
  };

  const onChangePagination = (page: number, pageSize: number) => {
    if (!payFrequency || !taxPeriod) return;
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    fetchBureauTimesheets({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
      payFrequency,
      taxPeriod,
      search: searchRef.current,
      companyRefs: companyRefsRef.current,
      filter: filterRef.current,
    });
  };

  const handleSavePayroll = async () => {
    if (selectedRows.length === 0 || !payFrequency || !taxPeriod) return;

    const salaryList = selectedRows.filter((e) => e?.isSalary === true);
    const timesheetList = selectedRows.filter((e) => e?.isSalary !== true);

    const params = {
      subcontractorIds: salaryList.map((e) => e.id),
      timesheetIds: timesheetList.map((e) => e.id),
      taxYear: getTaxWeekTaxYear().taxYear,
      taxPeriod,
      payFrequency,
    };

    await saveRunPayroll(params);
    showMessage({
      type: 'success',
      content: messages['timesheet.saveRunPayroll.success'],
    });
    setSelectedRows([]);
    fetchBureauTimesheets({
      page: 0,
      size: pagination.pageSize,
      payFrequency,
      taxPeriod,
      search: searchRef.current,
      companyRefs: companyRefsRef.current,
      filter: filterRef.current,
    });
    onSuccess?.();
  };

  const handleClose = () => {
    setSelectedRows([]);
    setData([]);
    setSearchText('');
    setIsFilterOpen(false);
    searchRef.current = '';
    companyRefsRef.current = [];
    filterRef.current = {};
    setPagination({ current: 0, pageSize: 20, totalDocs: 0 });
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['timesheet.selectPayroll']}
      isOpen={isOpen}
      toggle={handleClose}
      width={1000}
      minHeight="0px"
      noPaddingBody
    >
      <div className="flex justify-between items-center my-4 px-4">
        <div className="flex items-center gap-2">
          {isAdmin && (
            <AppSelectSync
              mode="multiple"
              placeholder={
                isBureau
                  ? messages['common.selectEmployer']
                  : messages['form.selectCompany']
              }
              type="company"
              apiType="searchCompany"
              onChangeValue={handleCompanyChange}
              className="w-[200px]"
            />
          )}
          <AppInputSearch value={searchText} onChange={handleSearch} />
          <AppButton
            name={messages['common.filter']}
            leftIcon={<FilterOutlined />}
            onClick={() => setIsFilterOpen(true)}
          />
        </div>
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={handleSavePayroll}
          loading={isSaveRunPayrollPending}
          disabled={selectedRows.length === 0 || isSaveRunPayrollPending}
        />
      </div>
      <AppTableContainer
        rowKey="id"
        data={data}
        loading={loading}
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        scroll={{ x: 1500, y: 700 }}
        titleEmpty={messages['table.empty']}
        columns={getSelectPayrollColumns({
          messages,
          payrollCountry,
          handleView: () => {},
          theme,
        })}
        onChangePagination={onChangePagination}
        rowSelection={{
          type: 'checkbox',
          onChange: (_: React.Key[], selectedRows: any[]) => {
            setSelectedRows(selectedRows);
          },
        }}
      />
      <SelectPayrollFilterModal
        isOpen={isFilterOpen}
        toggle={() => setIsFilterOpen(false)}
        onFilter={handleFilter}
      />
    </AppModalContainer>
  );
};

export default SelectPayrollModal;
